<template>
  <div>
    <Header></Header>
    <div class="row py-3 mb-4 justify-content-between pageTitleDiv">
      <div class="container pl-0">
        <div class="h1 ml-3 mb-0 d-flex align-items-center font-lg pageTitle">WhatsApp</div>
      </div>
    </div>

    <div v-if="connectionLoading" class="container whatsapp-page">
      <div class="card whatsapp-connection-card text-center p-5 text-muted">WhatsApp bağlantısı kontrol ediliyor...</div>
    </div>

    <div v-else-if="!whatsappConnected" class="container whatsapp-page">
      <div class="card whatsapp-connection-card text-center p-5">
        <i class="fab fa-whatsapp whatsapp-connection-icon"></i>
        <h4>WhatsApp bağlantısı yok</h4>
        <p class="text-muted mb-4">WhatsApp mesajlarınızı görüntülemek için önce entegrasyon bağlantısını yapın.</p>
        <div><button class="btn btn-success" @click="goToIntegrations">Entegrasyonlara Git</button></div>
      </div>
    </div>

    <div v-else class="container whatsapp-page">
      <div class="card whatsapp-card">
        <div class="row no-gutters whatsapp-layout">
          <aside class="col-md-4 whatsapp-conversations">
            <div class="p-3 border-bottom">
              <input v-model="search" class="form-control" placeholder="Konuşma ara">
              <button class="btn btn-success btn-sm btn-block mt-2" @click="beginNewConversation"><i class="fas fa-plus mr-1"></i>Yeni konuşma</button>
            </div>
            <div v-if="conversationsLoading" class="p-4 text-center text-muted">Konuşmalar yükleniyor...</div>
            <div v-else-if="filteredConversations.length === 0" class="p-4 text-center text-muted">Konuşma bulunamadı.</div>
            <button
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              class="whatsapp-conversation text-left"
              :class="{ active: activeConversation && activeConversation.id === conversation.id }"
              @click="selectConversation(conversation)"
            >
              <div class="whatsapp-avatar">{{ initials(conversation.name) }}</div>
              <div class="flex-grow-1 overflow-hidden">
                <div class="d-flex justify-content-between align-items-center">
                  <strong class="text-truncate">{{ conversation.name }}</strong>
                  <small class="text-muted ml-2">{{ formatDate(conversation.lastDate) }}</small>
                </div>
                <div class="d-flex justify-content-between align-items-center">
                  <span class="text-muted text-truncate">{{ conversation.lastMessage }}</span>
                  <span v-if="conversation.unread" class="badge badge-success ml-2">{{ conversation.unread }}</span>
                </div>
              </div>
            </button>
          </aside>

          <section class="col-md-8 whatsapp-chat">
            <template v-if="newConversationOpen">
              <header class="whatsapp-chat-header border-bottom px-3 py-2 d-flex justify-content-between align-items-center">
                <strong>Yeni WhatsApp konuşması</strong>
                <button class="btn btn-light btn-sm" @click="closeNewConversation">İptal</button>
              </header>
              <div class="p-3">
                <div v-if="newConversationError" class="alert alert-danger py-2">{{ newConversationError }}</div>
                <label class="new-label"><b>Kişi veya telefon numarası</b></label>
                <div class="input-group mb-2">
                  <input v-model.trim="contactSearch" class="form-control" placeholder="Kişi adı veya telefon numarası" @input="resetContactSearch" @keydown.enter.prevent="searchContacts">
                  <div class="input-group-append"><button class="btn btn-outline-secondary" :disabled="contactSearching || !contactSearch" @click="searchContacts">Ara</button></div>
                </div>
                <div v-if="contactSearching" class="text-muted mb-3">Kişiler aranıyor...</div>
                <div v-else-if="contactSearchAttempted && contactResults.length === 0" class="text-muted mb-3">Sonuç bulunamadı.</div>
                <div v-else-if="contactResults.length" class="list-group mb-3">
                  <button v-for="contact in contactResults" :key="contact.id" class="list-group-item list-group-item-action" :class="{ active: selectedContact && selectedContact.id === contact.id }" @click="selectNewContact(contact)">
                    <strong>{{ contact.name }}</strong><small v-if="contact.phone" class="d-block">{{ contact.phone }}</small>
                  </button>
                </div>
                <div v-if="selectedContact" class="alert alert-light border py-2">Seçili kişi: <strong>{{ selectedContact.name }}</strong></div>
                <button class="btn btn-success" :disabled="creatingConversation || !selectedContact" @click="createConversation">
                  {{ creatingConversation ? 'Konuşma açılıyor...' : 'Konuşmayı Başlat' }}
                </button>
              </div>
            </template>
            <template v-else-if="activeConversation">
              <header class="whatsapp-chat-header border-bottom px-3 py-2 d-flex align-items-center justify-content-between">
                <div class="overflow-hidden whatsapp-contact-summary" :class="{ 'whatsapp-contact-summary--clickable': activeConversation.contactId }" :role="activeConversation.contactId ? 'button' : undefined" :tabindex="activeConversation.contactId ? 0 : -1" @click="viewActiveContact" @keydown.enter.prevent="viewActiveContact">
                  <strong class="whatsapp-contact-name text-truncate">{{ activeConversation.name }}</strong>
                  <small v-if="activeConversation.phone" class="d-block text-muted">{{ activeConversation.phone }}</small>
                </div>
                <div class="whatsapp-contact-actions">
                  <button class="btn btn-light btn-sm" type="button" aria-label="Kişi işlemleri" :aria-expanded="contactMenuOpen" @click.stop="contactMenuOpen = !contactMenuOpen"><i class="fas fa-ellipsis-v"></i></button>
                  <div v-if="contactMenuOpen" class="whatsapp-contact-menu">
                    <button class="dropdown-item" type="button" :disabled="!activeConversation.contactId" @click="viewActiveContact">Kişiyi Görüntüle</button>
                    <button class="dropdown-item text-danger" type="button" :disabled="deletingConversation" @click="deleteActiveConversation">{{ deletingConversation ? 'Siliniyor...' : 'Sohbeti Sil' }}</button>
                  </div>
                </div>
              </header>
              <div ref="messagesContainer" class="whatsapp-messages p-3">
                <div v-if="messagesLoading" class="text-center text-muted">Mesajlar yükleniyor...</div>
                <div v-else-if="messages.length === 0" class="text-center text-muted mt-4">Mesaj bulunamadı.</div>
                <div v-for="message in messages" :key="message.id" class="d-flex mb-2" :class="message.outgoing ? 'justify-content-end' : 'justify-content-start'">
                  <div class="whatsapp-message" :class="[message.outgoing ? 'outgoing' : 'incoming', { 'whatsapp-message--image': message.mediaType === 'image' && message.mediaUrl }]">
                    <img v-if="message.mediaType === 'image' && message.mediaUrl" class="whatsapp-message-image" :src="getMessageMediaUrl(message)" :data-fallback-src="message.mediaFallbackUrl" :alt="message.body || 'WhatsApp görseli'" @error="showMediaFallback">
                    <a v-else-if="message.mediaUrl" class="whatsapp-message-file" :href="message.mediaUrl" target="_blank" rel="noopener noreferrer"><i class="fas fa-paperclip mr-1"></i>{{ message.body || 'Dosyayı görüntüle' }}</a>
                    <div v-else>{{ message.body }}</div>
                    <small>
                      {{ formatDate(message.date) }}
                      <span
                        v-if="message.outgoing"
                        class="whatsapp-receipt"
                        :class="`whatsapp-receipt--${getMessageReceipt(message.status).type}`"
                        :aria-label="getMessageReceipt(message.status).label"
                        :title="getMessageReceipt(message.status).label"
                      >{{ getMessageReceipt(message.status).ticks }}</span>
                    </small>
                  </div>
                </div>
              </div>
              <div class="border-top p-3">
                <div v-if="isSessionExpired || templateRequired" class="alert alert-warning mb-2 py-2 d-flex justify-content-between align-items-center"><span>{{ templateRequired ? 'Bu konuşmanın ilk mesajını şablon ile gönderin.' : '24 saatlik mesaj gönderim süresi doldu.' }}</span><button class="btn btn-warning btn-sm ml-2" @click="openTemplatePicker(activeConversation)">Şablon Gönder</button></div>
                <div v-if="sendError" class="alert alert-danger mb-2 py-2" role="alert">{{ sendError }}</div>
                <div v-if="templatePickerOpen" class="border rounded p-2 mb-2">
                  <div class="d-flex justify-content-between align-items-center mb-1"><label class="new-label mb-0">WhatsApp şablonu</label><button class="btn btn-link btn-sm p-0" type="button" title="Şablon seçiciyi kapat" aria-label="Şablon seçiciyi kapat" @click="templatePickerOpen = false"><i class="fas fa-times"></i></button></div>
                  <div class="d-flex">
                    <select v-model="selectedTemplateKey" class="form-control mr-2" :disabled="templatesLoading"><option value="">Şablon seçin</option><option v-for="template in templates" :key="template.key" :value="template.key">{{ template.name }} ({{ template.lang }})</option></select>
                    <button class="btn btn-success" :disabled="sendingTemplate || !selectedTemplate" @click="sendTemplateToActiveConversation">{{ sendingTemplate ? 'Gönderiliyor...' : 'Gönder' }}</button>
                  </div>
                </div>
                <form class="d-flex whatsapp-composer" @submit.prevent="sendMessage">
                  <input ref="attachmentInput" class="d-none" type="file" @change="selectAttachment">
                  <button class="btn mr-1 whatsapp-composer-icon" type="button" title="Şablon kullanın" aria-label="Şablon kullanın" :disabled="sendingTemplate" @click="openTemplatePicker(activeConversation)"><i class="far fa-file-alt"></i></button>
                  <button class="btn mr-2 whatsapp-composer-icon" type="button" title="Dosya ekleyin" aria-label="Dosya ekleyin" @click="openAttachmentPicker"><i class="fas fa-paperclip"></i></button>
                  <div v-if="selectedAttachment" class="whatsapp-attachment-chip mr-2" :title="selectedAttachment.name"><img v-if="selectedAttachmentPreview" class="whatsapp-attachment-preview mr-1" :src="selectedAttachmentPreview" alt="Seçilen görsel önizlemesi"><i v-else class="fas fa-paperclip mr-1"></i><span class="text-truncate">{{ selectedAttachment.name }}</span><button type="button" title="Dosyayı kaldır" aria-label="Dosyayı kaldır" @click="clearAttachment"><i class="fas fa-times"></i></button></div>
                  <textarea v-model.trim="messageBody" :disabled="isSessionExpired || templateRequired || sending" class="form-control mr-2 whatsapp-composer-textarea" rows="1" placeholder="Mesajınızı yazın" @keydown.enter.exact.prevent="sendMessage"></textarea>
                  <button :disabled="(!messageBody && !selectedAttachment) || isSessionExpired || templateRequired || sending" class="btn btn-success whatsapp-composer-send" type="submit">
                    {{ sending ? 'Gönderiliyor...' : 'Gönder' }}
                  </button>
                </form>
              </div>
            </template>
            <div v-else class="whatsapp-empty text-muted">Bir konuşma seçin.</div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import Swal from 'sweetalert2'
import Header from './header'

const getValue = (value) => value && value.$oid ? value.$oid : value
const getDate = (value) => value && value.$date ? value.$date : value
const normalizeDriveUrl = (url) => String(url || '').replace(/[?&]export=download$/i, '')
const getDriveFileId = (url) => {
  const normalizedUrl = normalizeDriveUrl(url)
  const driveFileId = /drive\.google\.com\/[^?#]*[?&]id=([^&#]+)/i.exec(normalizedUrl)
  return driveFileId ? driveFileId[1] : ''
}

export default {
  name: 'WhatsApp',
  components: { Header },
  data () {
    return {
      conversations: [],
      activeConversation: null,
      messages: [],
      search: '',
      messageBody: '',
      conversationsLoading: false,
      messagesLoading: false,
      sending: false,
      sendError: '',
      newConversationOpen: false,
      contactSearch: '',
      contactResults: [],
      selectedContact: null,
      contactSearching: false,
      contactSearchAttempted: false,
      creatingConversation: false,
      newConversationError: '',
      templates: [],
      templatesLoading: false,
      selectedTemplateKey: '',
      templatePickerOpen: false,
      sendingTemplate: false,
      templateRequired: false,
      contactMenuOpen: false,
      deletingConversation: false,
      selectedAttachment: null,
      selectedAttachmentPreview: '',
      connectionLoading: true,
      whatsappConnected: false,
      googleDriveConnected: false,
      pageSize: 10,
      socketRefreshInProgress: false
    }
  },
  computed: {
    ...mapState(['baseUrl']),
    filteredConversations () {
      const keyword = this.search.toLocaleLowerCase('tr-TR')
      return this.conversations.filter(conversation => conversation.name.toLocaleLowerCase('tr-TR').includes(keyword))
    },
    isSessionExpired () {
      if (!this.activeConversation || !this.activeConversation.sessionExpireAt) return false
      const expireAt = new Date(this.activeConversation.sessionExpireAt)
      return !isNaN(expireAt.getTime()) && expireAt.getTime() <= Date.now()
    },
    selectedTemplate () {
      return this.templates.find(template => template.key === this.selectedTemplateKey) || null
    }
  },
  created () {
    this.checkWhatsAppConnection()
  },
  mounted () {
    window.addEventListener('whatsapp_new_message', this.refreshFromWhatsAppSocket)
    window.addEventListener('whatsapp_status_update', this.refreshFromWhatsAppSocket)
  },
  beforeUnmount () {
    window.removeEventListener('whatsapp_new_message', this.refreshFromWhatsAppSocket)
    window.removeEventListener('whatsapp_status_update', this.refreshFromWhatsAppSocket)
  },
  methods: {
    async refreshFromWhatsAppSocket () {
      if (!this.whatsappConnected || this.socketRefreshInProgress) return
      this.socketRefreshInProgress = true
      try {
        await this.loadConversations(false)
        if (this.activeConversation) await this.loadMessagesForConversation(this.activeConversation)
      } finally {
        this.socketRefreshInProgress = false
      }
    },
    async checkWhatsAppConnection () {
      try {
        const response = await axios({ method: 'GET', url: '/getCloudApiTokens', baseURL: this.baseUrl, withCredentials: true })
        const status = response && response.data && response.data.status
        const isSuccess = status === true || status === 'true' || (status && (status.code === 0 || status.code === '0'))
        const tokens = response && response.data && response.data.content && response.data.content.tokens
        this.whatsappConnected = Boolean(isSuccess && Array.isArray(tokens) && tokens.some((token) => String(token.cloud_type || token.cloudType || '').toLowerCase() === 'whatsapp'))
        this.googleDriveConnected = Boolean(isSuccess && Array.isArray(tokens) && tokens.some((token) => ['google-drive', 'google_drive'].includes(String(token.cloud_type || token.cloudType || '').toLowerCase())))
        if (this.whatsappConnected) this.loadConversations()
      } catch (error) {
        this.whatsappConnected = false
      } finally {
        this.connectionLoading = false
      }
    },
    goToIntegrations () {
      this.$router.push({ path: '/settings', query: { tab: 'thirdPartySettings' } }).catch(() => {})
    },
    async beginNewConversation () {
      this.newConversationOpen = true
      this.activeConversation = null
      this.messages = []
      this.newConversationError = ''
    },
    closeNewConversation () {
      this.newConversationOpen = false
      this.contactSearch = ''
      this.contactResults = []
      this.selectedContact = null
      this.contactSearchAttempted = false
      this.newConversationError = ''
    },
    resetContactSearch () {
      this.contactSearchAttempted = false
      this.newConversationError = ''
    },
    normalizeContact (contact) {
      const id = String(getValue(contact._id || contact.id || contact.contactId) || '')
      const name = [contact.n || contact.name || contact.firstName, contact.sn || contact.surname || contact.lastName].filter(Boolean).join(' ') || `Kişi ${id}`
      const phoneNumbers = this.getContactPhoneNumbers(contact)
      return {
        id,
        name,
        phone: phoneNumbers.join(' · '),
        phoneNumbers
      }
    },
    getContactPhoneNumbers (contact) {
      const values = [contact.gsm, contact.phone, contact.phoneNumber, contact.gsmNumbers, contact.phoneNumbers]
      const phoneNumbers = []
      const addValue = value => {
        if (Array.isArray(value)) return value.forEach(addValue)
        if (value && typeof value === 'object') return addValue(value.val || value.value)
        if (value !== undefined && value !== null && String(value).trim()) phoneNumbers.push(String(value).trim())
      }
      values.forEach(addValue)
      return [...new Set(phoneNumbers)]
    },
    uniqueContacts (contacts) {
      const uniqueContacts = new Map()
      contacts.forEach(contact => {
        if (!contact.id) return
        const existing = uniqueContacts.get(contact.id)
        if (!existing) {
          uniqueContacts.set(contact.id, contact)
          return
        }
        const phoneNumbers = [...new Set([...existing.phoneNumbers, ...contact.phoneNumbers])]
        existing.phoneNumbers = phoneNumbers
        existing.phone = phoneNumbers.join(' · ')
      })
      return [...uniqueContacts.values()]
    },
    async hydrateContactPhoneNumbers (contacts) {
      const contactsWithoutPhone = contacts.filter(contact => contact.id && contact.phoneNumbers.length === 0)
      if (!contactsWithoutPhone.length) return contacts
      const responses = await Promise.all(contactsWithoutPhone.map(async (contact) => {
        try {
          const response = await axios({ method: 'POST', url: '/getContact', baseURL: this.baseUrl, data: { contactId: contact.id }, withCredentials: true })
          return { id: contact.id, contact: response.data && response.data.content && response.data.content.contact }
        } catch (error) {
          return { id: contact.id, contact: null }
        }
      }))
      const detailsById = responses.reduce((result, response) => {
        if (response.contact) result[response.id] = response.contact
        return result
      }, {})
      return contacts.map(contact => {
        const details = detailsById[contact.id]
        if (!details) return contact
        const phoneNumbers = this.getContactPhoneNumbers(details)
        return { ...contact, phoneNumbers, phone: phoneNumbers.join(' · ') }
      })
    },
    async searchContacts () {
      if (!this.contactSearch) return
      this.contactSearching = true
      this.contactSearchAttempted = true
      this.contactResults = []
      this.selectedContact = null
      this.newConversationError = ''
      try {
        const phoneSearch = this.contactSearch.replace(/\D/g, '')
        const isPhoneSearch = phoneSearch.length >= 2 && /^[\d\s()+-]+$/.test(this.contactSearch)
        const response = isPhoneSearch
          ? await axios({
            method: 'POST',
            url: '/getContacts',
            baseURL: this.baseUrl,
            data: {
              pagingSize: 50,
              pagingOffset: 0,
              filter: {
                searchMode: 'contacts',
                name: 'default',
                ands: [{ fieldName: 'gsm', filterType: 'contacts', operator: 'contains', value: phoneSearch }]
              }
            },
            withCredentials: true
          })
          : await axios({ method: 'POST', url: '/quickFilterContact', baseURL: this.baseUrl, data: { search: this.contactSearch }, withCredentials: true })
        const contacts = this.uniqueContacts(this.extractList(response, ['contacts', 'items', 'results']).map(this.normalizeContact))
        this.contactResults = await this.hydrateContactPhoneNumbers(contacts)
        if (!this.contactResults.length) this.newConversationError = 'Bu numara veya kişi için CRM kaydı bulunamadı.'
      } catch (error) {
        this.newConversationError = 'Kişiler aranamadı. Lütfen tekrar deneyin.'
      } finally {
        this.contactSearching = false
      }
    },
    selectNewContact (contact) {
      this.selectedContact = contact
      this.newConversationError = ''
    },
    normalizeTemplate (template, index) {
      const name = template.name || template.templateName || template.template_name || ''
      const lang = template.language || template.lang || template.templateLang || template.template_lang || ''
      return { name, lang, key: `${name}:${lang}:${index}` }
    },
    async loadWhatsAppTemplates () {
      if (this.templatesLoading) return
      this.templatesLoading = true
      try {
        const response = await axios({ method: 'POST', url: '/getWhatsappTemplates', baseURL: this.baseUrl, withCredentials: true })
        this.templates = this.extractList(response, ['whatsapp_templates', 'templates', 'whatsappTemplates', 'items', 'results']).map(this.normalizeTemplate).filter(template => template.name && template.lang)
        if (!this.templates.length) this.newConversationError = 'Gönderilebilir WhatsApp şablonu bulunamadı.'
      } catch (error) {
        this.newConversationError = 'WhatsApp şablonları yüklenemedi.'
      } finally {
        this.templatesLoading = false
      }
    },
    openTemplatePicker (conversation) {
      this.templatePickerOpen = true
      this.sendError = ''
      this.loadWhatsAppTemplates()
    },
    async createConversation () {
      if (!this.selectedContact) return
      this.creatingConversation = true
      this.newConversationError = ''
      const contact = this.selectedContact
      const contactId = contact.id
      try {
        const response = await axios({ method: 'POST', url: '/createWhatsAppConversation', baseURL: this.baseUrl, data: { contactId: this.selectedContact.id }, withCredentials: true })
        if (!this.isSuccessfulResponse(response)) throw new Error(this.getResponseError(response))
        const content = response.data && response.data.content
        const conversation = content && (content.conversation || content)
        const conversationId = getValue(conversation && (conversation._id || conversation.id || conversation.conversationId || conversation.conversation_id))
        if (!conversationId) throw new Error('Yeni konuşma kaydı oluşturuldu ancak konuşma kimliği alınamadı.')
        this.closeNewConversation()
        await this.loadConversations()
        const activeConversation = this.conversations.find(item => item.contactId === contactId) || {
          id: String(conversationId),
          conversationIds: [String(conversationId)],
          contactId,
          name: contact.name,
          phone: contact.phone,
          sessionExpireAt: null,
          unread: 0,
          lastMessage: '',
          lastDate: null
        }
        this.activeConversation = activeConversation
        this.messages = []
        this.templateRequired = true
        this.templatePickerOpen = true
        this.selectedTemplateKey = ''
        await this.loadWhatsAppTemplates()
      } catch (error) {
        this.newConversationError = error.message || 'Yeni konuşma başlatılamadı.'
      } finally {
        this.creatingConversation = false
      }
    },
    async sendTemplateToActiveConversation () {
      if (!this.activeConversation || !this.selectedTemplate) return
      this.sendingTemplate = true
      this.sendError = ''
      try {
        await this.sendTemplate(this.activeConversation.id)
        this.templatePickerOpen = false
        this.templateRequired = false
        await this.selectConversation(this.activeConversation)
      } catch (error) {
        this.sendError = error.message || 'WhatsApp şablonu gönderilemedi.'
      } finally {
        this.sendingTemplate = false
      }
    },
    async sendTemplate (conversationId) {
      const response = await axios({
        method: 'POST',
        url: '/sendWhatsAppMessage',
        baseURL: this.baseUrl,
        data: {
          conversationId,
          messageType: 'template',
          templateName: this.selectedTemplate.name,
          templateLang: this.selectedTemplate.lang
        },
        withCredentials: true
      })
      if (!this.isSuccessfulResponse(response)) throw new Error(this.getResponseError(response))
      return response
    },
    extractList (response, keys) {
      const content = response && response.data && response.data.content
      const candidate = content || (response && response.data) || {}
      for (const key of keys) {
        if (Array.isArray(candidate[key])) return candidate[key]
      }
      return Array.isArray(candidate) ? candidate : []
    },
    normalizeConversation (conversation) {
      const contact = conversation.contact || conversation.customer || conversation.person || {}
      const contactId = String(getValue(conversation.contact_id || conversation.contactId || contact._id || contact.id) || '')
      const name = conversation.name || conversation.contactName || conversation.customerName || [contact.name || contact.n || contact.firstName, contact.surname || contact.sn || contact.lastName].filter(Boolean).join(' ') || (contactId ? `Kişi ${contactId}` : 'İsimsiz kişi')
      const lastMessage = conversation.last_message || conversation.lastMessage || conversation.lastMessageText || conversation.preview || ''
      return {
        raw: conversation,
        id: String(getValue(conversation._id || conversation.id || conversation.conversationId)),
        conversationIds: [String(getValue(conversation._id || conversation.id || conversation.conversationId))],
        contactId,
        name,
        phone: conversation.phone || conversation.phone_number || conversation.phoneNumber || contact.phone || contact.gsm || '',
        unread: Number(conversation.unread_count || conversation.unread || conversation.unreadCount || 0),
        lastMessage: typeof lastMessage === 'object' ? (lastMessage.body || lastMessage.messageBody || '') : lastMessage,
        lastDate: getDate(conversation.last_message_date || conversation.lastMessageDate || conversation.updated_at || conversation.updatedAt || conversation.lastActivityAt),
        sessionExpireAt: getDate(conversation.session_expires_at || conversation.sessionExpireAt || conversation.sessionExpiresAt)
      }
    },
    groupConversationsByPerson (conversations) {
      const grouped = {}

      conversations.forEach((conversation) => {
        const name = conversation.name.trim().toLocaleLowerCase('tr-TR')
        const phone = String(conversation.phone || '').replace(/\D/g, '')
        const groupKey = conversation.contactId
          ? `contact:${conversation.contactId}`
          : (phone ? `phone:${phone}` : (name ? `name:${name}` : `conversation:${conversation.id}`))
        const existing = grouped[groupKey]

        if (!existing) {
          grouped[groupKey] = { ...conversation }
          return
        }

        existing.conversationIds = [...new Set([...existing.conversationIds, ...conversation.conversationIds])]
        existing.unread += conversation.unread

        const existingDate = new Date(existing.lastDate).getTime()
        const currentDate = new Date(conversation.lastDate).getTime()
        if (!isNaN(currentDate) && (isNaN(existingDate) || currentDate > existingDate)) {
          // Mesaj gönderiminde en güncel konuşma kaydını kullan.
          existing.id = conversation.id
          existing.lastMessage = conversation.lastMessage
          existing.lastDate = conversation.lastDate
          existing.sessionExpireAt = conversation.sessionExpireAt
        }

        if (!existing.phone && conversation.phone) existing.phone = conversation.phone
      })

      return Object.keys(grouped).map(key => grouped[key])
    },
    normalizeMessage (message) {
      // WhatsApp API'sindeki sender alanı mesajın şirket hesabından mı,
      // yoksa müşteri tarafından mı gönderildiğini belirtir. Bu bilgi,
      // balonun hangi tarafta gösterileceğinin asıl kaynağıdır.
      const sender = String(message.sender || '').trim().toLowerCase()
      const direction = String(message.direction || message.type || '').toLowerCase()
      const messageContent = message.messageBody || message.body || message.content || message.text || ''
      const messageType = String(message.messageType || message.message_type || message.mediaType || message.media_type || message.type || '').trim().toLowerCase()
      const contentObject = messageContent && typeof messageContent === 'object' ? messageContent : {}
      const mediaObject = message.media && typeof message.media === 'object' ? message.media : {}
      const rawMediaUrl = [
        message.mediaUrl, message.media_url, message.fileUrl, message.file_url, message.downloadUrl, message.download_url, message.url, message.link,
        mediaObject.url, mediaObject.link, mediaObject.fileUrl, mediaObject.file_url, mediaObject.downloadUrl, mediaObject.download_url,
        contentObject.mediaUrl, contentObject.media_url, contentObject.fileUrl, contentObject.file_url, contentObject.downloadUrl, contentObject.download_url, contentObject.url, contentObject.link,
        contentObject.image && (contentObject.image.url || contentObject.image.link),
        contentObject.document && (contentObject.document.url || contentObject.document.link),
        message.image && (message.image.url || message.image.link),
        message.document && (message.document.url || message.document.link),
        typeof messageContent === 'string' && /^https?:\/\//i.test(messageContent) ? messageContent : ''
      ].find(value => typeof value === 'string' && value.trim()) || ''
      // Drive'ın export=download parametresi resmin tarayıcıda indirme olarak
      // ele alınmasına neden olabilir. Sohbet içi görsel önizlemesinde kaldırılır.
      const normalizedMediaUrl = normalizeDriveUrl(rawMediaUrl)
      const isImage = /image|photo/.test(messageType) || (normalizedMediaUrl && /\.(avif|gif|jpe?g|png|webp)(?:[?#]|$)/i.test(normalizedMediaUrl))
      const driveFileId = getDriveFileId(normalizedMediaUrl)
      const mediaUrl = normalizedMediaUrl
      const body = typeof messageContent === 'object'
        ? (mediaObject.caption || messageContent.caption || messageContent.body || messageContent.messageBody || messageContent.content || messageContent.text || mediaObject.filename || mediaObject.name || messageContent.filename || messageContent.name || '')
        : (normalizedMediaUrl === messageContent ? (mediaObject.caption || message.caption || mediaObject.filename || message.filename || message.fileName || '') : (messageContent || mediaObject.caption || mediaObject.filename || mediaObject.name || ''))
      const outgoingSenders = ['me', 'self', 'user', 'agent', 'business', 'company', 'crm', 'outgoing', 'outbound', 'sent']
      return {
        raw: message,
        id: String(getValue(message._id || message.id || message.messageId || `${message.createdAt}-${message.body}`)),
        body,
        mediaUrl,
        driveFileId,
        mediaFallbackUrl: isImage ? normalizedMediaUrl : '',
        mediaType: isImage ? 'image' : (mediaUrl ? 'document' : ''),
        date: getDate(message.createdAt || message.created_at || message.sentAt || message.timestamp || message.date || message.addDate),
        status: String(message.status || message.messageStatus || message.state || '').trim().toLowerCase(),
        outgoing: outgoingSenders.includes(sender) || message.fromMe === true || message.isOutgoing === true || ['outgoing', 'outbound', 'sent'].includes(direction)
      }
    },
    getMessageReceipt (status) {
      if (['read', 'seen'].includes(status)) {
        return { type: 'read', ticks: '✓✓', label: 'Okundu' }
      }
      if (['delivered', 'received'].includes(status)) {
        return { type: 'delivered', ticks: '✓✓', label: 'Teslim edildi' }
      }
      return { type: 'sent', ticks: '✓', label: 'Gönderildi' }
    },
    showMediaFallback (event) {
      const image = event.target
      const fallbackUrl = image && image.dataset && image.dataset.fallbackSrc
      if (fallbackUrl && image.src !== fallbackUrl) image.src = fallbackUrl
    },
    getMessageMediaUrl (message) {
      if (!message.driveFileId) return message.mediaUrl
      return `${String(this.baseUrl || '').replace(/\/$/, '')}/getWhatsAppMediaPreview?fileId=${encodeURIComponent(message.driveFileId)}`
    },
    sortMessagesChronologically (messages) {
      return messages
        .map((message, index) => ({ message, index }))
        .sort((left, right) => {
          const leftTime = new Date(left.message.date).getTime()
          const rightTime = new Date(right.message.date).getTime()

          // Tarih bilgisi olmayan mesajlarda API'nin kendi sırasını koru.
          if (isNaN(leftTime) || isNaN(rightTime)) return left.index - right.index
          return leftTime === rightTime ? left.index - right.index : leftTime - rightTime
        })
        .map(item => item.message)
    },
    async loadConversations (selectRequestedConversation = true) {
      this.conversationsLoading = true
      try {
        const response = await axios({ method: 'POST', url: '/getWhatsAppConversations', baseURL: this.baseUrl, data: { limit: this.pageSize, skip: 0 }, withCredentials: true })
        this.conversations = this.groupConversationsByPerson(
          this.extractList(response, ['conversations', 'items', 'results']).map(this.normalizeConversation)
        )
        await this.loadContactNames()
        const requestedConversationId = String(this.$route.query.conversationId || '')
        if (selectRequestedConversation && requestedConversationId) {
          const requestedConversation = this.conversations.find(conversation => conversation.conversationIds.includes(requestedConversationId))
          await this.selectConversation(requestedConversation || {
            id: requestedConversationId,
            conversationIds: [requestedConversationId],
            contactId: '',
            name: 'WhatsApp konuşması',
            phone: '',
            unread: 0,
            lastMessage: '',
            lastDate: null,
            sessionExpireAt: null
          })
        }
      } catch (error) {
        this.notifyError('WhatsApp konuşmaları yüklenemedi.')
      } finally {
        this.conversationsLoading = false
      }
    },
    async loadContactNames () {
      const contactIds = [...new Set(this.conversations.map(conversation => conversation.contactId).filter(Boolean))]
      const responses = await Promise.all(contactIds.map(async (contactId) => {
        try {
          const response = await axios({ method: 'POST', url: '/getContact', baseURL: this.baseUrl, data: { contactId }, withCredentials: true })
          return { contactId, contact: response.data && response.data.content && response.data.content.contact }
        } catch (error) {
          return { contactId, contact: null }
        }
      }))

      const contacts = responses.reduce((result, response) => {
        if (response.contact) result[response.contactId] = response.contact
        return result
      }, {})

      this.conversations.forEach((conversation) => {
        const contact = contacts[conversation.contactId]
        if (!contact) return
        const name = [contact.n || contact.name || contact.firstName, contact.sn || contact.surname || contact.lastName].filter(Boolean).join(' ')
        if (name) conversation.name = name
        conversation.phone = conversation.phone || contact.gsm || contact.phone || contact.phoneNumber || ''
      })
    },
    async selectConversation (conversation) {
      this.activeConversation = conversation
      this.contactMenuOpen = false
      this.messageBody = ''
      this.clearAttachment()
      this.messages = []
      this.templateRequired = false
      this.templatePickerOpen = false
      await this.loadMessagesForConversation(conversation)
    },
    async loadMessagesForConversation (conversation) {
      this.messagesLoading = true
      try {
        const conversationIds = conversation.conversationIds || [conversation.id]
        const responses = await Promise.all(conversationIds.map((conversationId) => axios({
          method: 'POST',
          url: '/getWhatsAppMessages',
          baseURL: this.baseUrl,
          data: { conversationId, limit: this.pageSize, skip: 0 },
          withCredentials: true
        })))
        this.messages = this.sortMessagesChronologically(
          responses.reduce((messages, response) => {
            return messages.concat(this.extractList(response, ['messages', 'items', 'results']).map(this.normalizeMessage))
          }, [])
        )
        conversation.unread = 0
        Promise.all(conversationIds.map((conversationId) => axios({
          method: 'POST',
          url: '/markWhatsAppConversationAsRead',
          baseURL: this.baseUrl,
          data: { conversationId },
          withCredentials: true
        }))).then(() => {
          window.dispatchEvent(new Event('whatsapp-unread-count-refresh'))
        })
        this.$nextTick(this.scrollMessagesToBottom)
      } catch (error) {
        this.notifyError('WhatsApp mesajları yüklenemedi.')
      } finally {
        this.messagesLoading = false
      }
    },
    async sendMessage () {
      if ((!this.messageBody && !this.selectedAttachment) || this.isSessionExpired || !this.activeConversation) return
      this.sending = true
      this.sendError = ''
      const body = this.messageBody
      const attachment = this.selectedAttachment
      try {
        let response
        let fileUrl = ''
        if (attachment) {
          fileUrl = await this.uploadAttachmentToDrive(attachment)
          response = await axios({
            method: 'POST',
            url: '/sendWhatsAppMessage',
            baseURL: this.baseUrl,
            data: {
              conversationId: this.activeConversation.id,
              messageType: attachment.type && attachment.type.startsWith('image/') ? 'image' : 'document',
              messageBody: fileUrl,
              caption: body,
              filename: attachment.name
            },
            withCredentials: true
          })
        } else {
          response = await axios({ method: 'POST', url: '/sendWhatsAppMessage', baseURL: this.baseUrl, data: { conversationId: this.activeConversation.id, messageType: 'text', messageBody: body }, withCredentials: true })
        }
        if (!this.isSuccessfulResponse(response)) {
          throw new Error(this.getResponseError(response))
        }
        const responseMessages = this.extractList(response, ['messages', 'items', 'results'])
        const localMessageBody = attachment ? (body || attachment.name) : body
        this.messages.push(responseMessages.length ? this.normalizeMessage(responseMessages[0]) : {
          id: `local-${Date.now()}`,
          body: localMessageBody,
          mediaUrl: normalizeDriveUrl(fileUrl),
          driveFileId: getDriveFileId(fileUrl),
          mediaFallbackUrl: attachment && attachment.type && attachment.type.startsWith('image/') ? normalizeDriveUrl(fileUrl) : '',
          mediaType: attachment && attachment.type && attachment.type.startsWith('image/') ? 'image' : (attachment ? 'document' : ''),
          date: new Date().toISOString(),
          outgoing: true,
          status: 'sent'
        })
        this.activeConversation.lastMessage = localMessageBody
        this.activeConversation.lastDate = new Date().toISOString()
        this.messageBody = ''
        this.clearAttachment()
        this.$nextTick(this.scrollMessagesToBottom)
      } catch (error) {
        this.sendError = error && !error.response && error.message && error.message !== 'Network Error'
          ? error.message
          : 'WhatsApp mesajı gönderilemedi. Lütfen tekrar deneyin.'
        this.notifyError(this.sendError)
      } finally {
        this.sending = false
      }
    },
    isSuccessfulResponse (response) {
      const status = response && response.data && response.data.status
      if (status === undefined || status === true || status === 'true') return true
      return Boolean(status && (status.code === 0 || status.code === '0'))
    },
    getResponseError (response) {
      const status = response && response.data && response.data.status
      return (status && (status.desc || status.message)) || 'WhatsApp mesajı gönderilemedi. Lütfen tekrar deneyin.'
    },
    scrollMessagesToBottom () {
      const element = this.$refs.messagesContainer
      if (element) element.scrollTop = element.scrollHeight
    },
    formatDate (value) {
      if (!value) return ''
      const date = new Date(value)
      return isNaN(date.getTime()) ? '' : date.toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })
    },
    initials (name) {
      return name.split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase()
    },
    viewActiveContact () {
      if (!this.activeConversation || !this.activeConversation.contactId) return
      this.contactMenuOpen = false
      this.$router.push({ name: 'contactCard', params: { contactId: String(this.activeConversation.contactId) } }).catch(() => {})
    },
    selectAttachment (event) {
      const attachment = event.target.files && event.target.files[0]
      if (!attachment) return
      this.clearAttachment()
      this.selectedAttachment = attachment
      if (attachment.type && attachment.type.startsWith('image/')) this.selectedAttachmentPreview = URL.createObjectURL(attachment)
    },
    async uploadAttachmentToDrive (attachment) {
      const formData = new FormData()
      formData.append('myFile', attachment)
      const response = await axios({
        method: 'POST',
        url: '/uploadFile',
        baseURL: this.baseUrl,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      })
      if (!this.isSuccessfulResponse(response)) throw new Error(this.getResponseError(response))
      const content = response.data && response.data.content ? response.data.content : {}
      const file = content.file || content.customerFile || content
      const fileUrl = file.url || file.fileUrl || file.file_url || file.downloadUrl || file.download_url || content.url || content.fileUrl || content.file_url || content.downloadUrl || content.download_url
      if (!fileUrl) throw new Error('Drive yükleme yanıtında gönderilebilir dosya URL’si bulunamadı.')
      return fileUrl
    },
    async openAttachmentPicker () {
      if (this.googleDriveConnected) {
        this.$refs.attachmentInput.click()
        return
      }
      const result = await Swal.fire({
        title: 'Google Drive bağlantısı gerekli',
        text: 'Dosya eklemek için Drive bağlantısını gerçekleştirin.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#1991eb',
        confirmButtonText: 'Drive’ı Bağla',
        cancelButtonText: 'Tamam',
        customClass: {
          icon: 'custom-warning',
          cancelButton: 'custom-delete-cancel-button'
        },
        reverseButtons: true
      })
      if (result.isConfirmed) this.$router.push({ path: '/settings', query: { tab: 'documentManagementSettings' } }).catch(() => {})
    },
    clearAttachment () {
      if (this.selectedAttachmentPreview) URL.revokeObjectURL(this.selectedAttachmentPreview)
      this.selectedAttachment = null
      this.selectedAttachmentPreview = ''
      if (this.$refs.attachmentInput) this.$refs.attachmentInput.value = ''
    },
    async deleteActiveConversation () {
      if (!this.activeConversation || this.deletingConversation) return
      const conversation = this.activeConversation
      const conversationIds = conversation.conversationIds || [conversation.id]
      const confirmation = await Swal.fire({
        title: 'Sohbeti Sil',
        text: 'Bu sohbet silinsin mi? Bu işlem geri alınamaz.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Sohbeti Sil',
        cancelButtonText: this.$ml.get('CANCEL'),
        customClass: {
          icon: 'custom-delete-warning',
          cancelButton: 'custom-delete-cancel-button'
        },
        reverseButtons: true
      })
      if (!confirmation.isConfirmed) return
      this.deletingConversation = true
      this.sendError = ''
      try {
        const responses = await Promise.all(conversationIds.map((conversationId) => axios({
          method: 'POST',
          url: '/deleteWhatsAppConversation',
          baseURL: this.baseUrl,
          data: { conversationId },
          withCredentials: true
        })))
        if (!responses.every(this.isSuccessfulResponse)) throw new Error(this.getResponseError(responses.find(response => !this.isSuccessfulResponse(response))))
        const deletedIds = new Set(conversationIds.map(String))
        this.conversations = this.conversations.filter(item => !item.conversationIds.some(id => deletedIds.has(String(id))))
        this.activeConversation = null
        this.messages = []
        this.contactMenuOpen = false
      } catch (error) {
        this.sendError = error.message || 'Sohbet silinemedi. Lütfen tekrar deneyin.'
      } finally {
        this.deletingConversation = false
      }
    },
    notifyError (text) {
      if (this.$notify) this.$notify({ group: 'foo', type: 'error', text })
    }
  }
}
</script>

<style scoped>
.whatsapp-card { min-height: 620px; overflow: hidden; font-family: "Roboto", sans-serif; }
.whatsapp-connection-card { min-height: 300px; display: flex; flex-direction: column; justify-content: center; align-items: center; }
.whatsapp-connection-icon { color: #25d366; font-size: 52px; margin-bottom: 14px; }
.whatsapp-layout { min-height: 620px; }
.whatsapp-conversations { border-right: 1px solid #e9ecef; overflow-y: auto; max-height: 620px; }
.whatsapp-conversation { width: 100%; border: 0; border-bottom: 1px solid #f0f0f0; background: #fff; padding: 14px; display: flex; gap: 12px; }
.whatsapp-conversation:hover, .whatsapp-conversation.active { background: #f0f9f4; }
.whatsapp-avatar { height: 42px; width: 42px; min-width: 42px; border-radius: 50%; display: flex; justify-content: center; align-items: center; color: #fff; background: #25d366; font-size: 13px; font-weight: 600; }
.whatsapp-chat { display: flex; flex-direction: column; min-height: 620px; }
.whatsapp-chat-header { background: #fafafa; }
.whatsapp-contact-summary { max-width: calc(100% - 52px); padding: 4px 6px; border-radius: 4px; }
.whatsapp-contact-summary--clickable { cursor: pointer; transition: background-color .15s ease, color .15s ease; }
.whatsapp-contact-summary--clickable:hover, .whatsapp-contact-summary--clickable:focus { background: #eef3f9; outline: 0; }
.whatsapp-contact-summary--clickable:hover .whatsapp-contact-name, .whatsapp-contact-summary--clickable:hover small, .whatsapp-contact-summary--clickable:focus .whatsapp-contact-name, .whatsapp-contact-summary--clickable:focus small { color: #1991eb !important; }
.whatsapp-contact-name { display: block; max-width: 100%; color: #212529; font-weight: 700; }
.whatsapp-contact-actions { position: relative; margin-left: 12px; }
.whatsapp-contact-menu { position: absolute; z-index: 2; top: calc(100% + 4px); right: 0; min-width: 180px; padding: 4px 0; background: #fff; border: 1px solid rgba(0, 0, 0, .15); border-radius: .25rem; box-shadow: 0 .25rem .5rem rgba(0, 0, 0, .12); }
.whatsapp-composer-icon, .whatsapp-composer-send { height: 34px; padding-top: 4px; padding-bottom: 4px; }
.whatsapp-composer-icon { border: 0; background: transparent; color: #6c757d; }
.whatsapp-composer-icon:hover, .whatsapp-composer-icon:focus { background: #eef3f9; color: #1991eb; box-shadow: none; }
.whatsapp-composer-textarea { min-height: 34px; max-height: 90px; padding-top: 5px; padding-bottom: 5px; resize: vertical; }
.whatsapp-attachment-chip { display: flex; align-items: center; min-width: 0; max-width: 190px; height: 34px; padding: 0 7px; border-radius: 4px; background: #eef3f9; color: #1991eb; }
.whatsapp-attachment-preview { width: 24px; height: 24px; border-radius: 3px; object-fit: cover; }
.whatsapp-attachment-chip span { min-width: 0; }
.whatsapp-attachment-chip button { margin-left: 6px; padding: 0; border: 0; background: transparent; color: inherit; cursor: pointer; }
.whatsapp-messages { flex: 1; overflow-y: auto; max-height: 480px; background: #efeae2; }
.whatsapp-message { max-width: 78%; padding: 9px 11px; border-radius: 8px; white-space: pre-wrap; word-break: break-word; }
.whatsapp-message.outgoing { background: #d9fdd3; }
.whatsapp-message.incoming { background: #fff; }
.whatsapp-message--image { width: fit-content; padding: 4px; }
.whatsapp-message-image { display: block; max-width: min(320px, 100%); max-height: 320px; border-radius: 5px; object-fit: contain; }
.whatsapp-message-file { color: inherit; text-decoration: underline; }
.whatsapp-message small { display: block; margin-top: 4px; text-align: right; color: #6c757d; font-size: 10px; }
.whatsapp-receipt { margin-left: 3px; font-size: 12px; font-weight: 600; letter-spacing: -3px; }
.whatsapp-receipt--sent, .whatsapp-receipt--delivered { color: #6c757d; }
.whatsapp-receipt--read { color: #53bdeb; }
.whatsapp-empty { flex: 1; display: flex; align-items: center; justify-content: center; background: #f8f9fa; }
@media (max-width: 767px) { .whatsapp-conversations { max-height: 280px; border-right: 0; border-bottom: 1px solid #e9ecef; } .whatsapp-chat { min-height: 480px; } }
</style>
